import { ARROW_MAGIC_BYTES } from './file-type-detection';

/**
 * Detects if the data is Apache Arrow Feather2 format.
 *
 * Feather2 files start with the magic bytes "ARROW1" (in ASCII).
 * Since inline data comes as a base64-encoded string, we check if
 * the decoded data starts with these magic bytes.
 *
 * @param data - Base64-encoded string data
 * @returns True if data appears to be Feather2 format, false otherwise
 */
export function isFeather2FormatFromBase64(data: string): boolean {
  if (!data || data.length === 0) {
    return false;
  }

  try {
    // Decode base64 to binary string
    const binaryString = atob(data);
    // Check if it starts with "ARROW1" magic bytes
    return binaryString.startsWith('ARROW1');
  } catch {
    // If decoding fails, assume it's not Feather2
    return false;
  }
}

/**
 * Detects if a blob contains Apache Arrow Feather2 format data.
 *
 * @param blob - The blob to check
 * @returns Promise that resolves to true if data is Feather2 format
 */
export async function isFeather2FormatFromBlob(blob: Blob): Promise<boolean> {
  if (!blob || blob.size === 0) {
    return false;
  }

  try {
    // Read first 6 bytes to check for "ARROW1" magic bytes
    const headerSlice = blob.slice(0, 6);
    const arrayBuffer = await headerSlice.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    return ARROW_MAGIC_BYTES.every((byte, index) => bytes[index] === byte);
  } catch {
    return false;
  }
}
